/** @format */

import $cs_ from '@/js/plugins/cs-underscore-helpers';
import {validate, extend, configure} from 'vee-validate';
import * as rules from 'vee-validate/dist/rules';
import {$translate, $csI18n, validationFileLocaleCode} from '@/js/modules/csI18n';
import $csNetworkCall from '@/js/modules/csNetworkCall';

let $_locales = [];
let $_forms = {};
let $_timings = {};

const _apiBaseUrl = () => $cs_.getAppNode()?.$getApiBaseUrl();
const _getMaxFileNameLength = () => $cs_.getAppNode()?.$getMaxFileNameLength();

const $csValidations = {
	/**
	 * @public
	 * @description
	 * Gets a form field object by the given id and returns it
	 *
	 * @param {Object} destructured config object which can be updated as we need to add more things. This is
	 * typically the static items we send in from our Vue instances
	 *
	 * @returns {Void}
	 */
	setDefaults({apiBaseUrl, locales}) {		
		$_locales = locales;
	},

	/**
	 * @public
	 * @description
	 * Register a form id so we have a key to attach validation details to.
	 *
	 * @param {String} formGuid The id of the form we are registering
	 *
	 * @returns {Void}
	 */
	registerForm(formGuid) {
		if (!$_forms[formGuid]) {
			$_forms[formGuid] = {
				dictionary: {}
			};
		}
	},

	/**
	 * @public
	 * @description
	 * Use the timer for performance
	 *
	 * @param {String} formGuid The id of the form we are registering
	 *
	 * @returns {Void}
	 */
	startTimer(formGuid) {
		if (!$_timings[formGuid]) {
			$_timings[formGuid] = {
				start: undefined,
				end: undefined,
				total: undefined
			};
		}
		$_timings[formGuid].start = performance.now();
		$_timings[formGuid].end = undefined;
		$_timings[formGuid].total = undefined;
	},

	/**
	 * @public
	 * @description
	 * Stop timing and log output to the console
	 *
	 * @param {String} formGuid The id of the form we are registering
	 *
	 * @returns {Void}
	 */
	endTimer(formGuid) {
		if ($_timings[formGuid]) {
			$_timings[formGuid].end = performance.now();
			$_timings[formGuid].total = $_timings[formGuid].end - $_timings[formGuid].start;
		}
	},

	/**
	 * @public
	 * @description
	 * If we have provided a dictionary object we need to check for any items keyed by our
	 * target locale. This can be done either by the key matching the code (en === en) or
	 * the key will be a guid, in which case we need to look through the locales for our app
	 * and match the two digit code. We will then return these custom rules if we have any
	 *
	 * @param {Object} dictionary The custom translations dictionary object we have created
	 * @param {String} code The locale we are using for validation
	 *
	 * @returns {Object}
	 */
	getLocaleFieldMessagesFromDictionary(dictionary, code) {
		let localeFieldMessages = {};
		for (let item in dictionary) {
			if (item === code) {
				localeFieldMessages = dictionary[item].custom;
			} else {
				const activeLocale = $_locales.find((l) => l.id === item);
				if (activeLocale && activeLocale.twoDigitCode === code) {
					localeFieldMessages = dictionary[item].custom;
				}
			}
		}
		return localeFieldMessages;
	},

	/**
	 * @public
	 * @description
	 * Setter and getter for the dictionary contained in the given form.
	 */
	dictionary: {
		/**
		 * @public
		 * @description
		 * Add the dictionary object to the targeted form
		 *
		 * @param {String} formGuid The id of the form we are targeting
		 * @param {Object} dictionary The dictionary object to attach to the form
		 *
		 * @returns {Void}
		 */
		set(formGuid, dictionary) {
			$_forms[formGuid].dictionary = dictionary;
		},
		/**
		 * @public
		 * @description
		 * Get the dictionary object for the targeted form
		 *
		 * @param {String} formGuid The id of the form we are targeting
		 *
		 * @returns {Object}
		 */
		get(formGuid) {
			// sometimes the form might be destroyed before the validations finish, in which case the form will be undefined
			if ($_forms[formGuid] == undefined) return;
			return $_forms[formGuid].dictionary;
		}
	},

	/**
	 * @public
	 * @description
	 * Find a specific custom message for a field if it exists
	 *
	 * @param {String} formGuid The id of the form we are targeting
	 * @param {String} fieldId The id of the field in the form we are targeting
	 *
	 * @returns {Object}
	 */
	getCustomMessages(formGuid, fieldId) {
		const getMessages = $cs_.get($csValidations.dictionary.get(formGuid), fieldId);
		if (getMessages != undefined) return getMessages;
		return {};
	},

	/**
	 * @public
	 * @description
	 * Cleanup the module when we are finished with it
	 *
	 * @param {String} formGuid The id of the form we are targeting
	 *
	 * @returns {Void}
	 */
	destroy(formGuid) {
		if ($_forms[formGuid]) delete $_forms[formGuid];

		if (Object.keys($_forms).length === 0) {
			//no forms are registered, lets clear everything.
		}
	}
};

//MI-VALIDATION REWRITE

const _htmlTags = [
	'a',
	'abbr',
	'acronym',
	'address',
	'applet',
	'area',
	'article',
	'aside',
	'audio',
	'b',
	'base',
	'basefont',
	'bdi',
	'bdo',
	'bgsound',
	'big',
	'blink',
	'blockquote',
	'body',
	'br',
	'button',
	'canvas',
	'caption',
	'center',
	'cite',
	'code',
	'col',
	'colgroup',
	'content',
	'data',
	'datalist',
	'dd',
	'decorator',
	'del',
	'details',
	'dfn',
	'dir',
	'div',
	'dl',
	'dt',
	'element',
	'em',
	'embed',
	'fieldset',
	'figcaption',
	'figure',
	'font',
	'footer',
	'form',
	'frame',
	'frameset',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'head',
	'header',
	'hgroup',
	'hr',
	'html',
	'i',
	'iframe',
	'img',
	'input',
	'ins',
	'isindex',
	'kbd',
	'keygen',
	'label',
	'legend',
	'li',
	'link',
	'listing',
	'main',
	'map',
	'mark',
	'marquee',
	'menu',
	'menuitem',
	'meta',
	'meter',
	'nav',
	'nobr',
	'noframes',
	'noscript',
	'object',
	'ol',
	'optgroup',
	'option',
	'output',
	'p',
	'param',
	'plaintext',
	'pre',
	'progress',
	'q',
	'rp',
	'rt',
	'ruby',
	's',
	'samp',
	'script',
	'section',
	'select',
	'shadow',
	'small',
	'source',
	'spacer',
	'span',
	'strike',
	'strong',
	'style',
	'sub',
	'summary',
	'sup',
	'table',
	'tbody',
	'td',
	'template',
	'textarea',
	'tfoot',
	'th',
	'thead',
	'time',
	'title',
	'tr',
	'track',
	'tt',
	'u',
	'ul',
	'var',
	'video',
	'wbr',
	'xmp'
];

const _csVeeValidate = {};

/**
 * @public
 * @description
 * Custom validation method for checking the uniqueness of a username
 *
 * @param {String} value The value to check from the field being edited
 *
 * @returns {Promise}
 */
_csVeeValidate.isUniqueUsername = (value) => {
	let newtworkCallGuid = $csNetworkCall.createFetchGetRequest(`${_apiBaseUrl()}/api/identity/checkusername/${value}`);

	return $csNetworkCall
		.fetchRequest(newtworkCallGuid)
		.then((response) => {
			return {
				valid: response.data,
				data: {
					message: $translate.withArgs('validation.usernameTaken', [value])
				}
			};
		})
		.catch((error) => {
			$cs_.getAppNode().$recordError({
				type: 'error',
				error: error,
				displayToUser: true,
				errorCode: '000251'
			});
		});
};

/**
 * @public
 * @description
 * Custom validation method for checking the uniqueness of a given field
 *
 * @param {String} value The value to check from the field being edited
 * @param {Object} args The arguments to use to send on
 *
 * @returns {Promise}
 */
_csVeeValidate.isUniqueValue = (value, args) => {
	let payload = {
		collectionId: args.collectionId,
		fieldId: args.fieldId,
		itemId: args.itemId,
		value: value
	};

	let newtworkCallGuid = $csNetworkCall.createFetchPostRequest(`${_apiBaseUrl()}/api/GenericObjectController/ColumnHasUniqueValue`, payload);

	return $csNetworkCall
		.fetchRequest(newtworkCallGuid)
		.then((response) => {
			return {
				valid: response.data
			};
		})
		.catch((error) => {
			$cs_.getAppNode().$recordError({
				type: 'error',
				error: error,
				displayToUser: true,
				errorCode: '000305'
			});
		});
};

/**
 * @public
 * @description
 * Adds any additional custom validation methods we might need
 */
_csVeeValidate.addValidationMethods = ({messages}) => {
	try {
		Object.keys(rules).forEach((rule) => {
			let message = rule !== 'required' ? messages[rule] : $translate.withDefault('forms.fieldRequired');
			extend(rule, {
				...rules[rule], // copies rule configuration
				message: message // assign message
			});
		});

		extend('requiredSubFormRepeater', {
			validate: (value, args) => {
				let visibleRows = value.filter((row) => {
					return $cs_.getValueByObjectKeyIgnoringCase(row, args.deleteFieldId) !== args.deleteFieldValue;
				});
				return visibleRows.length > 0;
			},
			message: (fieldName) => {
				return $translate.withArgs('validation.fieldRequired', [fieldName]);
			},
			params: ['deleteFieldId', 'deleteFieldValue']
		});

		extend('uniqueValue', {
			validate: _csVeeValidate.isUniqueValue,
			message: (fieldName) => {
				return $translate.withArgs('validation.fieldMustBeUnique', [fieldName]);
			},
			params: ['collectionId', 'fieldId', 'itemId']
		});

		extend('uniqueUsername', {
			validate: _csVeeValidate.isUniqueUsername,
			message: (fieldName) => {
				return $translate.withArgs('validation.fieldMustBeUnique', [fieldName]);
			}
		});

		//THIS IS NO LONGER USED - FILEPOND DOES THIS
		/*extend('fileExtension', {
				validate: (value) => {
					let validate = true;
					const allowedExtensions = $cs_.getAppNode().$getAllowedFileExtension();
					if (Array.isArray(value)) {
						for (let f = 0, file; (file = value[f]); f++) {
							const fileName = file.name || file.Name;
							const fileExtension = typeof fileName === 'string' ? fileName.substring(fileName.lastIndexOf('.')) : null;

							if (fileExtension === null || allowedExtensions.includes(fileExtension.toLowerCase()) === false) {
								validate = false;
							}
						}
					}

					return validate;
				},
				message: (fieldName) => {
					return $translate.withArgs('validation.fileAcceptedTypes', [fieldName, $cs_.getAppNode().$getAllowedFileExtension().join(', ')]);
				}
			});*/

		extend('size', {
			validate: (value, args) => {
				let validate = true,
					maxAllowed = Math.abs(parseInt(args[0])) || 50000;
				if (Array.isArray(value)) {
					let combinedSize = 0;
					for (let f = 0, valueLength = value.length; f < valueLength; f++) {
						combinedSize += value[f].size || 0;
					}
					if (combinedSize > maxAllowed) {
						validate = false;
					}
				}
				return validate;
			},
			message: (fieldName, placeholders) => {
				return $translate.withArgs('validation.fileSizeExceeded', [fieldName, placeholders.size / 1000]);
			}
		});

		extend('fileNameMaxLength', {
			validate: (value) => {
				let validate = true,
					maxLength = _getMaxFileNameLength();

				if (Array.isArray(value)) {
					for (let f = 0, file; (file = value[f]); f++) {
						if (file.name.length > maxLength) {
							validate = false;
							break;
						}
					}
				}

				return validate;
			},
			message: (fieldName) => {
				return $translate.withArgs('validation.filenameMaxlength', [fieldName, _getMaxFileNameLength()]);
			}
		});

		extend('fileMaxNumber', {
			validate: (value, args) => {
				let validate = true,
					maxFiles = parseInt(args[0]);

				if (Array.isArray(value) && isFinite(maxFiles)) {
					if (value.length > maxFiles) validate = false;
				}
				return validate;
			},
			message: (fieldName) => {
				return `The ${fieldName} attachment count exceeds the maximum permitted number.`;
			}
		});

		extend('validColour', {
			validate: (value) => {
				const isValidRgb = () => {
					if (typeof value !== 'string') {
						return false;
					}

					return value.match(new RegExp('^rgb\\((25[0-5]|2[0-4][0-9]|1[0-9]?[0-9]?|[1-9][0-9]?|[0-9]), ?(25[0-5]|2[0-4][0-9]|1[0-9]?[0-9]?|[1-9][0-9]?|[0-9]), ?(25[0-5]|2[0-4][0-9]|1[0-9]?[0-9]?|[1-9][0-9]?|[0-9])\\)$')) !== null;
				};

				const isValidRgba = () => {
					if (typeof value !== 'string') {
						return false;
					}

					return value.match(new RegExp('^rgba\\((25[0-5]|2[0-4][0-9]|1[0-9]?[0-9]?|[1-9][0-9]?|[0-9]), ?(25[0-5]|2[0-4][0-9]|1[0-9]?[0-9]?|[1-9][0-9]?|[0-9]), ?(25[0-5]|2[0-4][0-9]|1[0-9]?[0-9]?|[1-9][0-9]?|[0-9]), ?(1|0|0.[0-9]+)\\)$')) !== null;
				};

				const validHex = () => {
					if (typeof value !== 'string') {
						return false;
					}
					return /^#[0-9A-F]{6}$/i.test(value.trim());
				};

				if (isValidRgb() || isValidRgba() || validHex()) return true;

				return $translate.withArgs('validation.validHex', [fieldName]);
			}
		});

		extend('decimal', {
			validate: (value, {decimals = '*', separator = '.'} = {}) => {
				if (value === null || typeof value === 'undefined' || value === '') {
					return false;
				}

				if (Array.isArray(value)) {
					return value.every((val) => validate(val, {decimals, separator}));
				}

				// if is 0.
				if (Number(decimals) === 0) {
					return /^-?\d*$/.test(value);
				}

				const regexPart = decimals === '*' ? '+' : `{1,${decimals}}`;
				const regex = new RegExp(`^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE][-]?\\d+)?$`);

				if (!regex.test(value)) {
					return false;
				}

				const parsedValue = parseFloat(value);

				// This is a JS hack that handles NaN results
				return parsedValue === parsedValue;
			}
		});

		extend('alpha_underscore', {
			validate: (value) => {
				return /^[a-zA-Z0-9_]([a-zA-Z0-9_])+$/g.test(value);
			},
			message: (fieldName) => {
				return $translate.withArgs('validation.alphaUnderscore', [fieldName]);
			}
		});

		extend('url', {
			validate: (value) => {
				var expression =
					/^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
				var regex = new RegExp(expression);

				return value.match(regex);
			},
			message: (fieldName) => {
				return $translate.withArgs('validation.validUrl', [fieldName]);
			}
		});

		extend('minRepeatersRequired', {
			validate: (value, args) => {
				if (args[0] && Array.isArray(value)) {
					const minRequired = parseInt(args[0]);
					if (value.length < minRequired) {
						return $translate.withArgs('validation.repeaterMin', [minRequired]);
					}
				}
				return true;
			},
			message: (fieldName) => {
				return $translate.withArgs('validation.repeaterMin', [fieldName]);
			}
		});

		extend('maxRepeatersAllowed', {
			validate: (value, args) => {
				if (args[0] && Array.isArray(value)) {
					const maxRequired = parseInt(args[0]);
					if (value.length > maxRequired) {
						return $translate.withArgs('validation.repeaterMax', [maxRequired]);
					}
				}
				return true;
			}
		});

		// we may need to bring in when we extend our use of i18n so will leave here for now
		// configure({
		// 	defaultMessage: (field, values) => {
		// 	  // override the field name.
		// 	  values._field_ = this.i18n.t(`fields.${field}`);

		// 	  return this.i18n.t(`validation.${values._rule_}`, values);
		// 	}
		// });
	} catch (error) {
		$cs_.getAppNode().$recordError({
			type: 'error',
			error: error,
			displayToUser: true,
			errorCode: '000250'
		});
	}
};

//PUBLIC

const $csVeeValidate = {};

/**
 * Registers vee validate
 */
$csVeeValidate.initializeVeeValidate = async () => {
	const code = validationFileLocaleCode();
	let locale = await import(`vee-validate/dist/locale/${code}.json`);
	_csVeeValidate.addValidationMethods(locale);
}

/**
 * @public
 * @description
 * Register our form in the csValidations module, then check the locales
 * against our `code` parameter to find if we have any custom messages assigned for our current locale.
 * We then import our specific locale json and pass that object on to `addValidationMethods` so we can use the appropriate messages
 *
 * @param {String} formGuid The id of the form that the field exists in
 * @param {Object} dictionary The dictionary objects of translations if they exist
 * @param {String} code The locale code that we wish to use
 *
 * @returns {Void}
 */
$csVeeValidate.setValidationDictionary = (formGuid, dictionary) => {
	const code = validationFileLocaleCode();
	$csValidations.setDefaults({
		apiBaseUrl: _apiBaseUrl(),
		locales: $cs_.getAppNode().$root.$data.locales
	});

	$csValidations.registerForm(formGuid);
	let localeFieldMessages = $csValidations.getLocaleFieldMessagesFromDictionary(dictionary, code);
	$csValidations.dictionary.set(formGuid, localeFieldMessages);	
};

/**
 * @public
 * @description
 * Validates an individual field within a section and returns the results
 * NOTE: VeeValidate will only return one error message when bails is set to true - the default setting for performance
 *
 * @param {String} formGuid The id of the form that the field exists in
 * @param {String} fieldId The id of the field in question
 * @param {Mixed} value The value to validate
 * @param {Object} rules The rules to validate against
 * @param {String} label The label of the value being validated 
 * @param {Object} passThroughItems An object containing any items the calling function wants back in the response
 *
 * @returns {Promise}
 */
$csVeeValidate.validateValue = (formGuid, fieldId, value, rules, label, passThroughItems = {}) => {
	return validate(value, rules, {
		name: label,
		bails: true,
		skipOptional: true,
		isInitial: true,
		customMessages: $csValidations.getCustomMessages(formGuid, fieldId)
	}).then((result) => {
		return {
			result,
			passThroughItems
		};
	});
};

/**
 * @public
 * @description
 * Validate and strip html tags that are not allowed
 * @param { String } value
 * @param { Array } optionalAdditionalTags
 * @returns { String } value
 */
$csVeeValidate.validateTags = (value, optionalAdditionalTags = []) => {
	if (typeof value != 'string') {
		const valueType = typeof value;

		console.warn(`CS: Invalid value type. Can\'t validate tags on: ${valueType.charAt(0).toUpperCase() + valueType.slice(1)}, exepcting String.`);

		return;
	}
	if (value.length == 0 || value == undefined) return value;

	const whitelist = $cs_.getAppNode().$getEditorAllowedTags(optionalAdditionalTags);

	let blockedTags = '';

	for (let i = 0; i < _htmlTags.length; i++) {
		if (whitelist.indexOf(_htmlTags[i]) == -1) {
			blockedTags += _htmlTags[i];
			if (i + 1 != _htmlTags.length) blockedTags += '|';
		}
	}

	const regex = new RegExp(`(<\/?(${blockedTags})([^>]+)?\/?>)|(&lt;\/?(${blockedTags})([^;]+)?\/?&gt;)`, 'gi');
	if (regex) value = value.replace(regex, '');

	return value;
};

export {$csValidations as default};
export {$csVeeValidate};
